import React, { useRef, useEffect } from 'react';
import { Group, Rect } from 'react-konva';
import { GroupShape as IGroupShape } from '../types';
import { getSnapPosition } from '../utils/snap';
import { SnapLines } from './SnapLines';
import { useMapCanvas } from './map/contexts/MapCanvasContext';
import { DraggableShape } from './map/shapes/DraggableShape/DraggableShape';
import { ShapeProvider } from './map/shapes/ShapeContext/ShapeProvider';
import { useShape } from './map/shapes/ShapeContext/useShape';
import { ShapeTransformer } from './map/shapes/ShapeTransformer';
import { ShapeCommonProps } from './map/types';

export const GroupShape: React.FC<ShapeCommonProps> = (commonProps) => {
  const {
    setSnapLines,
    snapLines,
    shape,
    isSelected,
    onSelect,
    onUpdate,
    snapToGrid,
    otherShapes,
    showSnapLines,
    disabled
  } = useShape();

  const { gridSize, isViewMode } = useMapCanvas();

  const groupShape = shape as IGroupShape;

  const groupRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);

  useEffect(() => {
    if (isSelected && transformerRef.current && groupRef.current) {
      transformerRef.current.nodes([groupRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const snapToGridValue = (value: number): number => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };

  const handleDragMove = (e: any) => {
    if (disabled) return;

    const node = e.target;
    let newX = node.x();
    let newY = node.y();

    if (snapToGrid) {
      newX = snapToGridValue(newX);
      newY = snapToGridValue(newY);
      node.position({ x: newX, y: newY });
    }

    if (showSnapLines) {
      const snapResult = getSnapPosition(
        shape,
        otherShapes || [],
        { x: newX, y: newY },
        groupShape.id
      );

      if (snapResult.position.x !== undefined) {
        newX = snapResult.position.x;
      }
      if (snapResult.position.y !== undefined) {
        newY = snapResult.position.y;
      }

      setSnapLines(snapResult.lines);
    } else {
      setSnapLines({});
    }

    node.position({ x: newX, y: newY });
  };

  const handleDragEnd = () => {
    if (!disabled) {
      const node = groupRef.current;
      onUpdate({
        x: node.x(),
        y: node.y()
      });
    }
    setSnapLines({});
  };

  const handleTransformEnd = () => {
    if (!disabled) {
      const node = groupRef.current;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      node.scaleX(1);
      node.scaleY(1);

      onUpdate({
        x: node.x(),
        y: node.y(),
        width: Math.max(5, node.width() * scaleX),
        height: Math.max(5, node.height() * scaleY)
      });
    }
  };

  const handleChildClick = (e: any) => {
    e.cancelBubble = true;
    onSelect(e);
  };

  return (
    <>
      {/* @ts-ignore*/}
      <Group ref={groupRef} {...commonProps}>
        <Rect
          width={groupShape.width}
          height={groupShape.height}
          stroke="transparent"
          fill="transparent"
        />
        {groupShape.children.map((childShape) => (
          <ShapeProvider
            key={childShape.id}
            shape={childShape}
            isSelected={false}
            onSelect={handleChildClick}
            onUpdate={() => {}}
            isGroupChild={true}
            snapToGrid={snapToGrid}
            gridSize={gridSize}
            otherShapes={otherShapes}
            showSnapLines={showSnapLines}
            disabled={disabled}
          >
            <DraggableShape key={childShape.id} />
          </ShapeProvider>
        ))}
      </Group>
      {isSelected && !disabled && <ShapeTransformer ref={transformerRef} />}
      {showSnapLines && <SnapLines snapLines={snapLines} />}
    </>
  );
};
